<template>
  <div class="course-card" :class="[$config.platform, $config.project, { 'not-started': !course.progress && !showProgress && !course.certificateNumber, completed, horizontal, track, dark, locked: course.status === 'locked' && !course.unlocked, 'simple-lms': simpleLms, upcoming: course.upcoming }]">
    <component
      :is="linkComponent"
      :to="url(showProgress ? 'player' : urlMode)"
      :href="url(showProgress ? 'player' : urlMode)"
      :target="linkTarget"
      :data-event="`${dataEvent}.click`"
    >
      <div class="course-card-container">
        <component
          :is="urlMode === 'track-library' ? 'a' : 'div'"
          :href="url('player')"
          :class="[urlMode]" :data-event="`${dataEvent}.click`"
        >
          <div class="course-card-image" :class="{ locked: course.status === 'locked' && !course.unlocked && $config.project === 'lms', 'in-progress': showProgress, 'in-progress-simple': simpleLms && purchased }">
            <span v-if="course.status === 'locked' && !course.unlocked && $config.project === 'lms' " class="locked"><LockedIcon /> Locked</span>
            <div v-if="course.technology && $config.platform === 'ds'" class="icon" :class="course.technology">
              <components :is="require(`../assets/svg/tech-responsive-${course.technology.replace(' ', '-')}.svg?vue-component`)" />
            </div>
            <img
              loading="lazy" :src="courseImage"
              :alt="course.name"
              @error="loadFallbackImage"
            >
          </div>
        </component>
        <div v-if="track" class="label" :class="`label-${course.type.toLowerCase()}`">
          {{ course.type }}
        </div>
        <div class="course-card-body">
          <slot v-if="course.type && course.type === 'Elective'" name="elective" />
          <div class="course-info">
            <span v-if="label" class="label" :class="course.label">
              <components :is="require(`../assets/svg/courses/icon-label-${course.label}.svg?vue-component`)" />{{ label }}
            </span>
            <component
              :is="heading"
              class="course-name"
              :class="{ 'h6': !horizontal, 'h5': horizontal, hidden: track && course.type === 'Elective' && $config.project !== 'site' }"
              :title="course.name"
            >
              {{ course.name }}
            </component>
            <div class="instructors">
              with {{ course.instructors }}
            </div>
            <Rating v-if="course.reviews" :reviews="course.reviews" />
            <p v-if="horizontal" class="body-2">
              {{ course.description }}
            </p>
            <Progress
              v-if="(showProgress || course.certificateNumber) && $config.project === 'lms'"
              :progress="course.progress"
              :data="course.lessons"
              :certificate="!!course.certificateNumber"
              medal
              class="info info-progress"
            />
            <div v-else-if="$config.project === 'site' || ((!simpleLms && !showProgress) || (simpleLms && !showProgress && course.purchased))" class="info">
              {{ course.duration }} {{ Number(course.duration) === 1 ? 'hour' : 'hours' }} of content
            </div>
            <slot name="button" />
          </div>
          <slot name="business" />
        </div>
      </div>
    </component>
  </div>
</template>

<script>
import Progress from './Progress';
import Rating from './Rating';
import LockedIcon from '../assets/svg/icon-locked.svg?vue-component';
import Tooltip from '../components/Tooltip';

export default {
  components: {
    Progress, Rating, LockedIcon, Tooltip,
  },
  props: {
    course: { type: Object, required: true },
    horizontal: Boolean,
    forceProgress: Boolean,
    dark: Boolean,
    track: Boolean,
    urlMode: { type: String, default: '', required: false },
    targetBlank: Boolean,
    heading: { type: String, default: 'div', required: false },
    buttons: Boolean,
    simpleLms: Boolean,
  },
  data: () => ({
    imageLoadAttempts: 0,
  }),
  computed: {
    courseImage() {
      if (this.course.smallImage && this.course.smallImage.startsWith('https')) {
        return this.course.smallImage;
      }

      return this.$webp(`${this.$config.imagesUrl}/courses/${this.course.smallImage}`);
    },
    linkComponent() {
      if (this.urlMode === 'track-library') {
        return 'div';
      }
      if (this.targetBlank) {
        return 'a';
      }

      return 'nuxt-link';
    },
    linkTarget() {
      if (this.targetBlank) {
        return '_blank';
      }

      return '_self';
    },
    dataEvent() {
      if (this.$route.name === 'resources-center-category-slug') {
        return 'popularresources';
      }

      if (this.course.type === 'similar') {
        return 'courses_you_may_like';
      }

      if (this.course.slug) {
        return this.course.slug.replace(/-/g, '_');
      }

      return '';
    },
    showProgress() {
      return ((this.track && this.forceProgress) || (!this.track && (this.course.progress || this.course.started))) && (!this.simpleLms || (this.purchased)) && !this.course.pendingPayment;
    },
    courseLessons() {
      if (this.course.type === 'similar' || (this.course.lessons && typeof this.course.lessons !== 'object')) {
        return this.course.lessons;
      }

      return this.course.lessons && this.course.lessons.total;
    },
    courseCompletedLessons() {
      return typeof this.course.lessons === 'object' && this.course.lessons.completed;
    },
    purchased() {
      return this.simpleLms && this.course.purchased;
    },
    completed() {
      return this.course.type === 'similar' ? this.course.completed : (this.courseLessons === this.courseCompletedLessons && (!this.simpleLms || this.purchased));
    },
    label() {
      // If the course is purchased or the user has an active subscription, don't show the label for free courses
      if (this.course.label === 'free' && (this.course.purchased || this.$store.getters['auth/user'].activeSubscription)) {
        return false;
      }

      if (this.course.label === 'trending') {
        return 'trending topic';
      }

      return this.course.label;
    },
  },
  methods: {
    url(mode = 'course') {
      if (this.course.slug && this.course.slug.startsWith('https')) {
        return this.course.slug;
      }

      if (mode === 'library' && this.$route.name.startsWith('business')) {
        return `/business/library/${this.course.slug}/`;
      }
      if (this.$config.project === 'site') {
        return `/courses/${this.course.slug}/`;
      }

      if (this.purchased) {
        return `/courses/${this.course.slug}/`;
      }

      if (mode === 'player') {
        if (!this.showProgress) {
          return `/courses/preview/${this.course.slug}/`;
        }
        return `/courses/${this.course.slug}/`;
      }

      return (mode === 'player' && this.course.status === 'public') || (mode === 'player' && this.course.status === 'locked' && this.course.unlocked) ? `/courses/${this.course.slug}/` : `/courses/preview/${this.course.slug}/`;
    },
    loadFallbackImage(e) {
      this.imageLoadAttempts += 1;

      if (this.imageLoadAttempts > 1) {
        return;
      }

      // eslint-disable-next-line no-param-reassign
      e.target.src = `${this.$config.imagesUrl}/courses/${this.course.smallImage}`;
    },
  },
};
</script>

<style lang="scss">
.course-card {
  margin-bottom: var(--spacing-24);
  transition: transform var(--duration) ease;

  > a {
    height: 100%;
  }

  &:hover {
    transform: scale(1.03);

    @include bp($bp-desktop) {
      &.locked {
        position: relative;

        .tooltip {
          display: block;
          bottom: 102%;
        }
      }
    }
  }

  a:not(.btn) {
    text-decoration: none;
    color: var(--main-dark-shade-0);
    display: block;

    &:hover {
      color: var(--main-dark-shade-0);
    }
  }

  .course-card-container {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    background: var(--true-white);
    position: relative;
    border-radius: 10px;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &.not-started {
    .course-card-body {
      .info {
        margin-top: auto;
      }
    }

    &:hover:not(:has(.action-btn.progress)) {
      .course-card-body .course-name {
        color: var(--accent);
      }
    }
  }

  &:not(.not-started):not(.upcoming):hover {
    .course-card-image {
      background: var(--main-dark);

      img {
        opacity: 0.3;
      }

      &:after {
        visibility: visible;
        opacity: 1;
        z-index: 1;
        width: 60px;
        height: 60px;
      }
    }
  }

  &.completed.lms {
    .course-card-image {
      > img {
        opacity: 0.5;
      }
    }
  }

  .course-card-image {
    height: 194px;
    position: relative;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    overflow: hidden;
    transition: background-color var(--duration) ease-in;

    &:after {
      content: "";
      background-image: url("../assets/svg/courses/play-circle.svg");
      image-rendering: -webkit-optimize-contrast;
      position: absolute;
      top: 50%;
      left: 50%;
      width: 55px;
      height: 55px;
      transform: translate(-50%, -50%);
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      visibility: hidden;
      opacity: 0;
      z-index: -1;
      transition: width var(--duration), height var(--duration), opacity 300ms, visibility 300ms, z-index 300ms ease-in;
    }

    &.locked {
      background-color: var(--main-dark);

      img {
        mix-blend-mode: luminosity;
      }
    }

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    .icon {
      position: absolute;
      right: 20px;
      top: 20px;
      z-index: 1;

      img,
      svg {
        max-width: 28px;
        height: 100%;
        width: 100%;
        filter: drop-shadow(0 2px 10px rgba(0, 12, 31, 0.5));
      }
    }

    .locked {
      position: absolute;
      background-color: var(--main-red);
      box-shadow: 0 16px 30px rgba(0, 12, 31, 0.15);
      border-radius: 5px;
      color: var(--main-light);
      font-weight: 600;
      font-size: 14px;
      top: 12px;
      left: 16px;
      padding: 4px 8px;
      z-index: 1;
      display: flex;
      align-items: center;

      svg {
        width: 17px;
        margin-right: 4px;
      }
    }
  }

  &.simple-lms {
    .course-card-body {
      .action-btn {
        margin-top: auto;
        position: relative;

        &:hover {
          .tooltip {
            display: block;
          }
        }
      }

      &:has(.action-btn) .info {
        margin-bottom: var(--spacing-8);
      }

      .promo {
        font-size: 18px;
        margin-bottom: var(--spacing-8);

        .percent-off {
          color: var(--main-red);
          font-weight: 700;
        }

        .price {
          text-decoration: line-through;
          color: var(--main-dark-light-4);
        }
      }
    }

    &:not(.completed):hover {
      .btn-primary {
        background: var(--accent-shade-2);
      }
    }

    &:not(.not-started):hover {
      .course-card-image.in-progress:not(.locked):after,
      .course-card-image.in-progress-simple:not(.locked):after {
        visibility: visible;
        opacity: 1;
        z-index: 1;
        width: 60px;
        height: 60px;
      }
    }

    &:hover {
      .course-card-image {
        background-color: var(--main-dark-light-2);
      }

      .action-btn:not(.btn-dark-shade-4) {
        background: var(--accent);
        color: var(--main-light);
      }
    }
  }

  .course-card-body {
    padding: var(--spacing-12) var(--spacing-16) var(--spacing-16);
    flex-grow: 1;

    .label {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 6px 8px;
      border-radius: 5px;
      width: max-content;
      margin-bottom: 4px;
      font-size: 12px;
      font-weight: 600;
      line-height: 130%;
      text-transform: uppercase;
      color: rgba(0, 12, 31, 0.8);

      &.trending {
        background: var(--main-purple-light-2);
      }

      &.bestseller {
        background: var(--main-yellow-light-2);
      }

      &.top-rated {
        background: var(--accent-light-3);
      }

      &.new {
        background: var(--accent-shade-2);
        color: var(--main-light);
      }

      &.popular {
        background: var(--main-red-light-3);
      }

      &.free {
        background: var(--main-green-light-3);
      }
    }

    .course-info {
      display: flex;
      flex-direction: column;
      height: 100%;

      .course-rating {
        margin-bottom: var(--spacing-12);

        .ratings {
          color: var(--main-dark-light-4);
        }
      }

      .info {
        display: flex;
        align-items: center;
        line-height: 24px;
        font-size: 14px;
        margin-top: auto;

        span:not(.progress-bar) {
          margin-right: var(--spacing-8);
          font-size: 14px;
          font-weight: 500;
        }

        .dot {
          width: 4px;
          height: 4px;
          background: var(--main-dark);
          display: inline-block;
          border-radius: 50%;
        }
      }

      .course-name {
        font-size: 16px;
        font-weight: 600;
        line-height: 24px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        margin-bottom: var(--spacing-4);
      }

      .instructors {
        color: var(--main-dark-light-3);
        line-height: 167%;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        -webkit-box-orient: vertical;
      }
    }
  }

  &.horizontal {
    .course-card-container {
      flex-direction: row;
      padding: 10px;
      border-radius: 7px;

      .course-card-image {
        flex: 0 0 auto;
        width: 300px;
        border-radius: 7px;
        height: 195px;

        .icon svg {
          max-width: 29px;
        }
      }

      .course-card-body {
        flex: 1 1 auto;
        margin-left: 10px;
        height: 100%;
        padding-top: 0;
        padding-bottom: 0;
        min-width: 0;

        .course-name {
          margin-bottom: 0;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          max-width: 80%;
          min-height: unset;
          display: block;

          &.hidden {
            display: none;
          }
        }

        .body-2 {
          color: var(--main-dark-shade-3);
          padding-right: 50px;
          max-height: 72px;
          overflow: hidden;
          margin: 0 0 var(--spacing-12);
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .instructors {
          font-weight: 400;
          line-height: 150%;
          color: var(--main-dark);
          margin: var(--spacing-4) 0;
        }

        .info {
          margin-bottom: 0;
        }

        .info-progress {
          max-width: 70%;
        }
      }
    }

    &.lms {
      .course-card-container {
        .course-card-image {
          width: 320px;
          height: 216px;
        }

        .course-card-body .course-name {
          min-height: 40px;
        }
      }
    }
  }

  &.track {
    .label {
      position: absolute;
      width: 92px;
      height: 28px;
      top: 10px;
      right: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 3px;
      background-color: rgba(84, 92, 105, 0.1);
      font-weight: 600;
      font-size: 14px;
      line-height: 14px;

      &.label-required {
        background-color: rgba(61, 175, 184, 0.2);
        color: var(--accent-shade-2);
      }

      &.label-elective {
        background-color: rgba(224, 178, 52, 0.2);
        color: var(--main-yellow-dark-4);
      }
    }
  }

  &.dark {
    .course-card-container {
      background: var(--main-dark-light-2);
      color: var(--main-light);

      .course-card-body {
        .course-name,
        .info,
        .rating {
          color: var(--main-light);
        }

        .dot {
          background: var(--main-light);
        }

        .instructors {
          color: var(--main-light-shade-4);
        }
      }
    }
  }

  &.fa {
    .course-card-body .label {
      &.top-rated {
        background: var(--accent-light-2-200);
      }

      &.new {
        background: var(--accent);
      }
    }
  }
}

@include bp($bp-mobile) {
  .course-card {
    &.track {
      .label {
        position: static;
        margin: 12px 0 0 24px;
      }
    }

    &.horizontal {
      .course-card-container {
        flex-direction: column;
        padding: 0;

        .label {
          margin: 20px 20px var(--spacing-12);
        }

        .course-card-image {
          width: auto;
          height: 180px;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        .course-card-body {
          flex: 0 1 auto;
          min-width: 0;
          margin: 0;
          padding: 0 20px var(--spacing-20);

          .course-name {
            font-size: 18px;
            white-space: unset;
            max-width: 100%;
            overflow: unset;
            display: block;
          }

          .body-2 {
            padding: 0;
            -webkit-line-clamp: 3;
          }

          .info-progress {
            max-width: 90%;
          }
        }
      }

      &.lms {
        .course-card-container {
          .course-card-image {
            width: auto;
            height: 180px;
          }

          .course-card-body .body-2 {
            display: none;
          }
        }
      }
    }

    &.ds.lms {
      .course-card-body a .course-name {
        min-height: unset;
        margin: 0;
      }
    }
  }
}

.fa {
  @import '../assets/sass/fa/components/courses-card.scss';
}
</style>
